import React, {useState, useEffect, useRef} from "react";
import {useSelector, useDispatch} from "react-redux";
import useOnClickOutside from "use-onclickoutside";
import {useToasts} from "react-toast-notifications";
import { useReactToPrint } from "react-to-print";
import {format} from "date-fns";
import axios from 'axios';

import * as actionTypes from "../../store/actions/actionTypes";
import UrDt from "../CustomLogic/UrDt";
import AdmDt from "../CustomLogic/AdmDt";
import * as admissionActions from "../../store/actions/admission";
import {finishUploadPdf, removeCommentFile} from "../../store/actions/customUI";
import * as constants from "../../utils/constants";
import Spinner from "../Spinner";
import FileUploaderMultiple from "../FileUploaderMultiple";
import AdmissionBulkDocumentsPdf from "../AdmissionBulkDocumentsPdf";

const ModalComments = () => {
    const {show, admission, customUI, commentsType, modalHeader, printEnable, managerStatuses, ignoreCommentEnable} = useSelector(state => state.modal.comments);
    const [comment, setComment] = useState('');
    const [status, setStatus] = useState('0');
    const [loading, setLoading] = useState(true);
    const [comments, setComments] = useState([]);
    // Attachment (upload) state - only used when the column has upload_enable.
    const [files, setFiles] = useState([]);
    const [pendingFileIds, setPendingFileIds] = useState([]);
    const [uploaderKey, setUploaderKey] = useState(0);
    const [previewFile, setPreviewFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const {addToast} = useToasts();
    const dispatch = useDispatch();
    const idToken = useSelector(state => state.auth.idToken);
    const ref = React.useRef(null);
    useOnClickOutside(ref, () => hideModal());

    const uploadEnable = !!(customUI && customUI.upload_enable);

    // Seed the uploaded-files list from the admission snapshot each time the
    // modal opens for an upload-enabled column.
    useEffect(() => {
        if (show && uploadEnable && admission) {
            const uiData = admission.manager_customUI && admission.manager_customUI[customUI.id];
            setFiles(uiData && uiData.files ? uiData.files : []);
            setPendingFileIds([]);
            setUploaderKey(prev => prev + 1);
            setPreviewFile(null);
        }
    }, [show, uploadEnable, admission, customUI])

    const refreshFilesFrom = (updatedAdmission) => {
        if (!updatedAdmission) return;
        const uiData = updatedAdmission.manager_customUI && updatedAdmission.manager_customUI[customUI.id];
        setFiles(uiData && uiData.files ? uiData.files : []);
    }

    // Finalise every pending temporary upload, then refresh the file list and
    // reset the uploader so more files can be added.
    const doApproveFiles = async () => {
        setUploading(true);
        try {
            let updatedAdmission = null;
            for (const pendingId of pendingFileIds) {
                updatedAdmission = await dispatch(finishUploadPdf(admission, customUI, pendingId));
            }
            refreshFilesFrom(updatedAdmission);
            setPendingFileIds([]);
            setUploaderKey(prev => prev + 1);
        } finally {
            setUploading(false);
        }
    }

    const doRemoveFile = async (removeFileId) => {
        setUploading(true);
        try {
            const updatedAdmission = await dispatch(removeCommentFile(admission, customUI, removeFileId));
            refreshFilesFrom(updatedAdmission);
        } finally {
            setUploading(false);
        }
    }

    const hideModal = () => dispatch({type: actionTypes.MODAL_COMMENTS_HIDE});
    const sendStatus = () => dispatch(admissionActions.addComment(admission, comment, commentsType, status, addToast));
    const updateComment = event => setComment(event.target.value);
    const updateStatus = event => setStatus(event.target.value);

    const canSubmitComment = () => (comment !== '') && (status !== '0');

    useEffect(() => {
        if (show && admission) {
            setLoading(true);
            axios.get(
                `${process.env.REACT_APP_API_PATH}/admission/${admission.id}/comments/${commentsType}`,
                {headers: {'Authorization': idToken}}
            )
            .then((response) => {
                console.log('[actions/modal] comments loaded');
                setComments(response.data.comments || []);
                setLoading(false);
            })
            .catch((error) => {
                console.error('Error loading comments:', error);
                setComments([]);
                setLoading(false);
            });
        }
    }, [show, admission, commentsType, idToken])

    const renderHeader = () => {
        if (modalHeader) {
            return <h3>{modalHeader} for {admission.title} {admission.surname}, {admission.name}</h3>;
        }
        switch (commentsType) {
            case constants.TYPE_DISCHARGE_COMMENTS:
                return <h3>Discharge comments for {admission.title} {admission.surname}, {admission.name}</h3>;
        }
        return null;
    }

    const renderAttachments = () => {
        // Inline PDF preview (AdmissionBulkDocumentsPdf fetches the file as a
        // blob and renders it in an iframe with Print/Download).
        if (previewFile) {
            return (
                <div className="form-group mt-3">
                    <div className="d-flex justify-content-between align-items-center">
                        <strong>Preview - {previewFile.name}</strong>
                        <button type="button" className="btn btn-outline-primary" onClick={() => setPreviewFile(null)}>
                            Back to the list
                        </button>
                    </div>
                    <AdmissionBulkDocumentsPdf
                        admissionId={admission.id}
                        action={`${process.env.REACT_APP_API_PATH}/admission/${admission.id}/custom-ui/${customUI.id}/comment-download-file/${previewFile.id}`}
                        title={previewFile.name} downloadName={previewFile.name} width="100%"/>
                </div>
            );
        }

        return (
            <div className="form-group mt-3">
                <hr/>
                <strong>Attachments</strong>
                {files.length === 0
                    ? <p><em>No files uploaded yet.</em></p>
                    : <ul className="list-group mb-3">
                        {files.map(file => (
                            <li key={file.id} className="list-group-item d-flex justify-content-between align-items-center">
                                <span>
                                    {file.originalName}
                                    {file.uploadedAt && <small className="text-muted"> — {file.uploadedAt}</small>}
                                </span>
                                <span>
                                    <button type="button" className="btn btn-sm btn-outline-primary mr-2"
                                            onClick={() => setPreviewFile({id: file.id, name: file.originalName})}>View</button>
                                    <button type="button" className="btn btn-sm btn-outline-danger" disabled={uploading}
                                            onClick={() => doRemoveFile(file.id)}>Remove</button>
                                </span>
                            </li>
                        ))}
                    </ul>
                }
                <FileUploaderMultiple
                    api={`${process.env.REACT_APP_API_PATH}/admission/${admission.id}/upload-pdf2`}
                    onChange={setPendingFileIds}
                    resetKey={uploaderKey}
                />
                <div className="row">
                    <div className="col-12 d-flex justify-content-end">
                        <input type="button" value="Upload files" className="btn btn-primary"
                               disabled={pendingFileIds.length === 0 || uploading} onClick={doApproveFiles}/>
                    </div>
                </div>
            </div>
        );
    }

    const renderModal = () => {
        return (
            <div className="modal-dialog" role="document" ref={ref}>
                <div className="modal-content modal-height-500">
                    <button type="button" className="close" data-dismiss="modal" aria-label="Close" onClick={hideModal}>
                        <span aria-hidden="true">×</span>
                    </button>
                    <div className="modal-body lifehouse-comments">
                        <div className="container">
                            <div className="row">
                                <div className="col-9">
                                    {renderHeader()}
                                    <dl className="person-list mb-3">
                                        <UrDt admission={admission}/>
                                        <AdmDt admission={admission}/>
                                    </dl>
                                </div>
                                {printEnable && <_Print comments={comments}/>}
                            </div>
                            <strong>Message</strong>
                            <textarea onChange={updateComment} value={comment}></textarea>
                            <div className="form-group">
                                <div className="row">
                                    <div className="col-12 d-flex justify-content-end">
                                        <select onChange={updateStatus}>
                                            <option value={0}>Select status</option>
                                            <option value={1}>Pending</option>
                                            <option value={2}>Closed</option>
                                            {managerStatuses && managerStatuses.split(',').map(statusId => {
                                                const id = parseInt(statusId.trim(), 10);
                                                const label = constants.COMMENT_STATUS_LABELS[id];
                                                return label ? <option key={id} value={id}>{label}</option> : null;
                                            })}
                                        </select>
                                        <input className="btn btn-primary" type="button" value="Save comment"
                                               onClick={sendStatus} disabled={!canSubmitComment()}/>
                                    </div>
                                </div>
                            </div>
                            {loading ? (
                                <Spinner />
                            ) : (
                                comments.map(renderComment)
                            )}
                            {uploadEnable && renderAttachments()}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const renderStatus = status => {
        switch (status) {
            case constants.LIFEHOUSE_COMMENTS_STATUS.STATUS_REVIEW:
                return (
                    <button type="button" className="btn-group-table-button btn btn-gray10 btn-purple btn-25px">Review</button>
                );
            case constants.LIFEHOUSE_COMMENTS_STATUS.STATUS_PENDING:
                return (
                    <button type="button" className="btn-group-table-button btn btn-gray10 lifehouse-button-orange btn-25px">Pending</button>
                );
            case constants.LIFEHOUSE_COMMENTS_STATUS.STATUS_CLOSED:
                return (
                    <button type="button" className="btn-group-table-button btn btn-25px lifehouse-button-green">Closed</button>
                )
            case constants.LIFEHOUSE_COMMENTS_STATUS.STATUS_PAC_IN_PROGRESS:
                return (
                    <button type="button" className="btn-group-table-button btn btn-gray10 lifehouse-button-orange btn-25px">In Progress</button>
                );
            case constants.LIFEHOUSE_COMMENTS_STATUS.STATUS_PAC_REQUIRED:
                return (
                    <button type="button" className="btn-group-table-button btn btn-25px lifehouse-button-green">Required</button>
                )
            case constants.LIFEHOUSE_COMMENTS_STATUS.STATUS_PAC_NOT_REQUIRED:
                return (
                    <button type="button" className="btn-group-table-button btn btn-25px lifehouse-button-red">Not required</button>
                )
            case constants.LIFEHOUSE_COMMENTS_STATUS.STATUS_INSTRUCTIONS:
                return (
                    <button type="button" className="btn-group-table-button btn btn-pink btn-25px">Instructions</button>
                )
        }
    }

    const renderComment = item => {
        return (
            <>
                <hr/>
                <div className="row">
                    <div className="col-6">
                        <strong className={item.isIgnored ? 'ignored-comment' : ''}>{item.body}</strong><br />
                        <i>{item.createdBy} {item.createdAt}</i>
                        {item.isIgnored ? <><br/><i>Ignored by {item.ignoredBy} {item.ignoredAt}</i></> : null}
                    </div>
                    <div className="col-6 d-flex justify-content-end flex-badges">
                        {renderStatus(item.status)}
                        {ignoreCommentEnable && <_More item={item} commentsType={commentsType} modalHeader={modalHeader} printEnable={printEnable} managerStatuses={managerStatuses} ignoreCommentEnable={ignoreCommentEnable}/>}
                    </div>
                </div>
            </>
        )
    }

    return (
        <>
            <div className={`modal fade ${show ? 'show' : ''}`} id="documentStatusModal" tabIndex="-1">
                {show && renderModal()}
            </div>
        </>
    );
}

const _Print = ({comments}) => {
    const {show, admission, commentsType, modalHeader} = useSelector(state => state.modal.comments);
    const printRef = useRef(null);

    const header = modalHeader ? modalHeader : 'Comments';

    const formatStatus = (status) => {
        switch (status) {
            case constants.LIFEHOUSE_COMMENTS_STATUS.STATUS_PENDING:
                return 'Pending';
            case constants.LIFEHOUSE_COMMENTS_STATUS.STATUS_CLOSED:
                return 'Closed';
            case constants.LIFEHOUSE_COMMENTS_STATUS.STATUS_PAC_IN_PROGRESS:
                return 'In Progress';
            case constants.LIFEHOUSE_COMMENTS_STATUS.STATUS_PAC_REQUIRED:
                return 'Required';
            case constants.LIFEHOUSE_COMMENTS_STATUS.STATUS_PAC_NOT_REQUIRED:
                return 'Not required';
            case constants.LIFEHOUSE_COMMENTS_STATUS.STATUS_REVIEW:
                return 'Review';
            case constants.LIFEHOUSE_COMMENTS_STATUS.STATUS_INSTRUCTIONS:
                return 'Instructions';
            default:
                return 'Unknown';
        }
    };

    const handlePrint = useReactToPrint({
        content: () => printRef.current,
        documentTitle: header,
        pageStyle: `
      * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
        }

        body {
            font-family: Arial, sans-serif;
            line-height: 1.4;
            color: #000;
            background-color: #fff;
            padding: 20px;
        }

        .container {
            max-width: 210mm;
            margin: 0 auto;
            background: white;
            padding: 30px;
        }

        .header {
            margin-bottom: 30px;
            padding-bottom: 15px;
            border-bottom: 2px solid #000;
        }

        .main-title {
            font-size: 24px;
            font-weight: bold;
            margin-bottom: 5px;
        }

        .patient-name {
            font-size: 18px;
            margin-bottom: 10px;
        }

        .generation-date {
            font-size: 12px;
            text-align: right;
        }

        .sub-header {
            margin-bottom: 25px;
            font-size: 16px;
        }

        .sub-header-item {
            display: inline-block;
            margin-right: 40px;
            margin-bottom: 5px;
        }

        .sub-header-label {
            font-weight: bold;
        }

        .section-title {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 15px;
            border-bottom: 1px solid #000;
            padding-bottom: 5px;
        }

        .comments-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 30px;
        }

        .comments-table th,
        .comments-table td {
            border: 1px solid #000;
            padding: 10px;
            text-align: left;
            vertical-align: top;
        }

        .comments-table th {
            font-weight: bold;
            background-color: #fff;
        }

        .staff-name {
            font-weight: normal;
        }

        .date-time {
            font-size: 13px;
            white-space: nowrap;
        }

        .comment-text {
            line-height: 1.4;
        }

        .footer {
            margin-top: 30px;
            padding-top: 15px;
            border-top: 1px solid #000;
            text-align: center;
            font-size: 12px;
        }

        @media print {
            body {
                padding: 0;
            }
            
            .container {
                padding: 20px;
                max-width: none;
            }
        }
    `,
    });

    return <div className="col-2">
        <button className="btn btn-white btn-time-opener" onClick={handlePrint}><span className="icon-calendar"><svg
            width="1em" height="1em" viewBox="0 0 16 16" className="bi bi-printer"
            fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path
            d="M11 2H5a1 1 0 0 0-1 1v2H3V3a2 2 0 0 1 2-2h6a2 2 0 0 1 2 2v2h-1V3a1 1 0 0 0-1-1zm3 4H2a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h1v1H2a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2h-1v-1h1a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1z"></path><path
            fill-rule="evenodd"
            d="M11 9H5a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1v-3a1 1 0 0 0-1-1zM5 8a2 2 0 0 0-2 2v3a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H5z"></path><path
            d="M3 7.5a.5.5 0 1 1-1 0 .5.5 0 0 1 1 0z"></path></svg></span><span>Print</span>
        </button>
        <div  className="display-none">
            <div ref={printRef}>
                <div className="container">
                    <div className="header">
                        <div className="logo"><img src={`${process.env.REACT_APP_API_PATH}/hospital/${admission.hospital.id}/logo`} width={300}/></div>
                        <h1 className="main-title">{header}</h1>
                        <div className="patient-name">{admission.title} {admission.surname}, {admission.name}</div>
                        <div className="sub-header">
                            <div className="sub-header-item">
                                <span className="sub-header-label">MRN:</span> {admission.URNumber}
                            </div>
                            <div className="sub-header-item">
                                <span className="sub-header-label">Visit No:</span> {admission.ADMNumber}
                            </div>
                        </div>
                        <div className="generation-date">Generated: {format(new Date(), "LLLL dd, yyyy 'at' h:mm aa")}</div>
                    </div>

                    <div className="comments-section">
                        <h2 className="section-title">Staff Comments</h2>

                        <table className="comments-table">
                            <thead>
                            <tr>
                                <th>Date & Time</th>
                                <th>Status</th>
                             <th>Staff Member</th>
                                <th>Comment</th>
                            </tr>
                            </thead>
                            <tbody>
                            {comments && comments.length > 0 ? (
                                comments.map((comment, index) => (
                                    <tr key={index}>
                                        <td className="date-time">{comment.createdAt}</td>
                                        <td>{formatStatus(comment.status)}</td>
                                        <td className="staff-name">{comment.createdBy}</td>
                                        <td className="comment-text">{comment.body}</td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="4" className="text-center">No comments available</td>
                                </tr>
                            )}
                            </tbody>
                        </table>
                    </div>

                    <div className="footer">
                        <p>This document was generated on {format(new Date(), "LLLL dd, yyyy 'at' h:mm aa")}</p>
                        <p>{admission.hospital.title}</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
}

const _More = ({item, commentsType, modalHeader, printEnable, managerStatuses, ignoreCommentEnable}) => {
    const popupRef = useRef(null);
    const [popupOpen, setPopupOpen] = useState(false);
    const hidePopup = () => setPopupOpen(false);
    useOnClickOutside(popupRef, hidePopup);

    const togglePopup = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setPopupOpen(!popupOpen);
    }

    return <div className="more-options-drop btn lifehouse-comments-more" ref={popupRef}>
        <button type="button" className="dropdown-toggle dropdown-toggle-split" onClick={togglePopup}
                id={`adm_dropdownMenuReference_comment_${item.id}`} data-toggle="dropdown" aria-haspopup="true"
                aria-expanded="false" data-reference="parent">
            <span><i className="icon ico-more"/></span>
        </button>
        {popupOpen && <div className="dropdown-menu dropdown-menu-right show"
                           aria-labelledby={`adm_dropdownMenuReference_comment_${item.id}`}>
            <_IgnoreLink item={item} commentsType={commentsType} modalHeader={modalHeader} printEnable={printEnable} managerStatuses={managerStatuses} ignoreCommentEnable={ignoreCommentEnable}/>
        </div>}
    </div>
}

const _IgnoreLink = ({item, commentsType, modalHeader, printEnable, managerStatuses, ignoreCommentEnable}) => {
    const dispatch = useDispatch();
    const {addToast} = useToasts();

    const handleIgnore = (event) => {
        event.preventDefault();
        event.stopPropagation();
        dispatch(admissionActions.ignoreComment(item.id, item.admissionId, commentsType, modalHeader, printEnable, managerStatuses, ignoreCommentEnable, addToast));
    }

    if (item.isIgnored) {
        return <a className="dropdown-item disabled pointer">Ignore</a>;
    }
    return <a className="dropdown-item pointer" onClick={handleIgnore}>Ignore</a>;
}

export default ModalComments;